import { useEffect, useState } from "react";

import { Pharmacy } from "@/Common/Api/Pharmacy/Dto/Responses/Pharmacy";
import { HyperLinkRedirectControlPatientProfileSubTabs } from "@/Common/Components/Buttons/HyperLinkRedirectControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { Guid } from "@/Common/Models/Guid";
import { ProfileTab } from "@/Common/Models/Patient/PatientEnums";
import { RedirectTo } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { RouterHistory } from "@/Common/Utilities/Router/RouterHistory";
import { DirectoryPharmacy } from "@/ContactDirectory/Models/DirectoryPharmacy";
import { PharmacyProxy } from "@/ContactDirectory/Models/PharmacyProxy";
import { Icon } from "@/DesignSystem";
import { IconBadge } from "@/DesignSystem/Molecules/IconBadge/IconBadge";

import styles from "./PreferredPharmacyStatus.module.scss";

import MedfarLogo from "@/DesignSystem/Atoms/Icons/Types/MedfarMedfarLogo/medfar-medfar-logo.svg";
import MedfarRx from "@/DesignSystem/Atoms/Icons/Types/MedfarRx/medfar-rx.svg";

interface IPreferredPharmacyStatusProps {
    patientContactId?: Guid;
    customPharmacy: Pharmacy;
    directoryPharmacyId: string;
    directoryPharmacy: DirectoryPharmacy;
    skipToLoadDirectoryPharmacy?: boolean;
    includeRelativeMaxWidth?: boolean;
}

type PreferredPharmacyType = "MYLE" | "DIRECTORY" | "CUSTOM" | "NONE";

const iconMapper: Readonly<Record<PreferredPharmacyType, JSX.Element>> = {
    "MYLE": (
        <Icon
            size="sm"
            colorVariant="brand"
            dataCy="preferred-pharmacy-status-myle"
        >
            <MedfarLogo />
        </Icon>
    ),
    "DIRECTORY": (
        <Icon
            size="sm"
            dataCy="preferred-pharmacy-status-directory"
        >
            <MedfarRx />
        </Icon>
    ),
    "CUSTOM": <IconBadge variant="warning" size="xs" dataCy="preferred-pharmacy-status-custom" />,
    "NONE": <IconBadge variant="error" size="xs" dataCy="preferred-pharmacy-status-none" />
};

const labelMapper: Readonly<Record<PreferredPharmacyType, string>> = {
    "MYLE": "MylePharmacyTooltipLabel",
    "DIRECTORY": "DirectoryPharmacyTooltipLabel",
    "CUSTOM": "CustomPharmacyTooltipLabel",
    "NONE": "NoPharmacyTooltipLabel"
};

function PreferredPharmacyStatus(props: Readonly<IPreferredPharmacyStatusProps>): JSX.Element {
    const { getText } = useTranslation();
    const [preferredPharmacy, setPreferredPharmacy] = useState<Readonly<{ name: string, type: PreferredPharmacyType }>>(
        {
            name: null,
            type: null
        }
    );

    useEffect(
        () => {
            let active = true;

            async function loadPreferredPharmacy(): Promise<void> {
                if ((!props.customPharmacy || Object.values(props.customPharmacy).every(p => !p)) && !props.directoryPharmacyId) {
                    setPreferredPharmacy({ name: null, type: "NONE" });
                    return;
                }

                const preferredPharmacy = new PharmacyProxy({
                    customPharmacy: props.customPharmacy,
                    directoryPharmacyId: props.directoryPharmacyId,
                    directoryPharmacy: props.directoryPharmacy
                });

                if (!props.skipToLoadDirectoryPharmacy) {
                    try {
                        if (preferredPharmacy.DirectoryPharmacyId && !preferredPharmacy.getDirectoryPharmacy()) {
                            await preferredPharmacy.loadDirectoryPharmacy();
                        }
                    }
                    catch {
                    // No action is implemented for failure in the api request.
                    }
                }

                if (!active) {
                    return;
                }

                const directoryPharmacy = preferredPharmacy.getDirectoryPharmacy();
                const displayName = preferredPharmacy.getDisplayName();

                if (directoryPharmacy) {
                    setPreferredPharmacy({
                        name: displayName,
                        type: directoryPharmacy.isMylePharmacy ? "MYLE" : "DIRECTORY"
                    });
                }
                else {
                    setPreferredPharmacy({ name: displayName, type: preferredPharmacy.DirectoryPharmacyId ? "DIRECTORY" : "CUSTOM" });
                }
            }

            setPreferredPharmacy({ name: null, type: null });
            loadPreferredPharmacy();

            return () => {
                active = false;
            };
        },
        [props.customPharmacy, props.directoryPharmacyId, props.directoryPharmacy, props.skipToLoadDirectoryPharmacy]
    );

    const onClick = (): void => {
        if (!props.patientContactId) {
            return;
        }

        const { location } = RouterHistory;

        if (location.pathname.includes("/patients")) {
            sessionStore.dispatch(
                LocalPatientOperations.Profile.OtherInfo_Redirect,
                {
                    switchToEditMode: true
                }
            );
        }
        else {
            sessionStore.dispatch(
                LocalOperations.Misc.RedirectToTab,
                {
                    value: props.patientContactId,
                    target: RedirectTo.Patient,
                    currentLocation: location,
                    navigateToSubTab: new HyperLinkRedirectControlPatientProfileSubTabs(ProfileTab.Other)
                }
            );
        }
    };

    if (!preferredPharmacy.type) {
        return null;
    }

    const preferredPharmacyName = preferredPharmacy.name ?? getText("NoPreferredPharmacyLabel");
    const icon = ["MYLE", "DIRECTORY"].includes(preferredPharmacy.type) ? styles.icon : "";

    return (
        <div
            className={`${styles.container} ${styles.marginLeft} ${props.patientContactId ? "" : styles.disabled} ${props.includeRelativeMaxWidth ? styles.relative : ""}`}
            title={getText(labelMapper[preferredPharmacy.type])}
            onClick={onClick}
            role="button"
            tabIndex={-1}
            data-cy="preferred-pharmacy-status-component"
        >
            <span className={`${styles.iconContainer} ${styles.noMarginLeft} ${icon}`}>{iconMapper[preferredPharmacy.type]}</span>
            <span title={preferredPharmacyName} className={`${styles.name} ${styles.marginLeft}`}>{preferredPharmacyName}</span>
        </div>
    );
}

export { PreferredPharmacyStatus };
